import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TripService } from '../services/tripService';
import type { TripRequest } from '../types/trip';

const TripRequestDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [tripRequest, setTripRequest] = useState<TripRequest | null>(null);

  useEffect(() => {
    document.title = 'Trip Request Detail';
  }, []);

  useEffect(() => {
    if (!id) {
      setError('Trip request ID is required');
      setLoading(false);
      return;
    }

    let isMounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const request = await TripService.getTripRequestById(id);
        if (!isMounted) return;
        setTripRequest(request);
      } catch (e) {
        if (!isMounted) return;
        setError(e instanceof Error ? e.message : 'Failed to load trip request');
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const formatDate = (iso: string | undefined | null | { $date?: string }) => {
    if (!iso) return '—';
    let dateStr: string;
    if (typeof iso === 'object' && iso.$date) {
      dateStr = iso.$date;
    } else if (typeof iso === 'string') {
      dateStr = iso;
    } else {
      return '—';
    }
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString();
  };

  const formatValue = (value: any): string => {
    if (value == null) return '—';
    if (typeof value === 'string') return value || '—';
    if (typeof value === 'number') return value.toLocaleString('en-IN');
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') {
      if (value.$date) return formatDate(value);
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const getRequestData = (r: TripRequest): any => {
    const d: any = (r as any).data;
    return d && typeof d === 'object' ? d : null;
  };

  const getUserDetails = (r: TripRequest) => {
    const user: any = r.userId ?? r.user;
    if (!user) return null;
    if (typeof user === 'string') return { _id: user };
    if (typeof user === 'object') {
      return {
        _id: user._id || '—',
        name: user.name || '—',
        email: user.email || '—',
        phone: user.phone || user.mobile || '—',
        avatar: user.avatar || '—',
        role: user.role || '—',
        isActive: user.isActive !== undefined ? user.isActive : '—',
        createdAt: user.createdAt || '—',
        ...user,
      };
    }
    return null;
  };

  const getTripDetails = (r: TripRequest) => {
    const data = getRequestData(r);
    return {
      tripName: data?.tripName ?? r.tripName ?? '—',
      originAirport: data?.originAirport ?? r.originAirport ?? '—',
      destinationAirport: data?.destinationAirport ?? r.destinationAirport ?? '—',
      hotelType: data?.hotelType ?? r.hotelType ?? '—',
      dateFrom: data?.dateFrom ?? r.dateFrom ?? '—',
      dateTo: data?.dateTo ?? r.dateTo ?? '—',
      passengers: data?.passengers ?? r.passengers ?? data?.numberOfTravelers ?? '—',
      infants: data?.infants ?? r.infants ?? '—',
      children: data?.children ?? r.children ?? '—',
      travelClass: data?.travelClass ?? r.travelClass ?? '—',
      mobile: data?.mobile ?? r.mobile ?? '—',
      rewardPoints: data?.rewardPoints ?? r.rewardPoints ?? '—',
      ...data,
    };
  };

  const downloadAsText = () => {
    if (!tripRequest) return;

    const userDetails = getUserDetails(tripRequest);
    const tripDetails = getTripDetails(tripRequest);

    let content = 'TRIP REQUEST DETAILS\n';
    content += '='.repeat(50) + '\n\n';

    content += 'USER DETAILS\n';
    content += '-'.repeat(50) + '\n';
    if (userDetails) {
      Object.entries(userDetails).forEach(([key, value]) => {
        if (key !== '_id' || value !== '—') {
          const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
          content += `${label}: ${formatValue(value)}\n`;
        }
      });
    } else {
      content += 'No user details available\n';
    }

    content += '\nTRIP DETAILS\n';
    content += '-'.repeat(50) + '\n';
    Object.entries(tripDetails).forEach(([key, value]) => {
      const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
      content += `${label}: ${formatValue(value)}\n`;
    });

    content += '\nREQUEST METADATA\n';
    content += '-'.repeat(50) + '\n';
    content += `Request ID: ${tripRequest._id}\n`;
    content += `Created At: ${formatDate(tripRequest.createdAt)}\n`;
    content += `Updated At: ${formatDate(tripRequest.updatedAt)}\n`;

    // Generate meaningful filename: name-source-destination-date
    const name = userDetails?.name && userDetails.name !== '—' 
      ? String(userDetails.name).replace(/[^a-zA-Z0-9]/g, '-').toLowerCase() 
      : 'user';
    const source = tripDetails.originAirport && tripDetails.originAirport !== '—'
      ? String(tripDetails.originAirport).replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()
      : 'source';
    const destination = tripDetails.destinationAirport && tripDetails.destinationAirport !== '—'
      ? String(tripDetails.destinationAirport).replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()
      : 'destination';
    
    // Use dateFrom if available, otherwise use created date
    let dateStr = 'date';
    if (tripDetails.dateFrom && tripDetails.dateFrom !== '—') {
      // Extract date from dateFrom (format might be DD/MM/YYYY or ISO)
      const dateValue = String(tripDetails.dateFrom);
      if (dateValue.includes('/')) {
        // Format: DD/MM/YYYY -> YYYY-MM-DD
        const parts = dateValue.split('/');
        if (parts.length === 3) {
          dateStr = `${parts[2]}-${parts[1]}-${parts[0]}`;
        } else {
          dateStr = dateValue.replace(/[^0-9]/g, '-');
        }
      } else {
        // Try to parse as ISO date
        try {
          const date = new Date(dateValue);
          if (!isNaN(date.getTime())) {
            dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD
          }
        } catch {
          dateStr = dateValue.replace(/[^0-9]/g, '-');
        }
      }
    } else {
      // Fallback to created date
      const createdDate = formatDate(tripRequest.createdAt);
      if (createdDate !== '—') {
        try {
          const date = new Date(createdDate);
          if (!isNaN(date.getTime())) {
            dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD
          }
        } catch {
          dateStr = 'date';
        }
      }
    }

    const filename = `${name}-${source}-${destination}-${dateStr}.txt`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading && !tripRequest) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <p className="mt-2 text-gray-500">Loading trip request...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Trip Request Detail</h1>
          <button
            onClick={() => navigate('/trips/requests')}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Back
          </button>
        </div>
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm">{error}</div>
      </div>
    );
  }

  if (!tripRequest) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Trip Request Detail</h1>
          <button
            onClick={() => navigate('/trips/requests')}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Back
          </button>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-md p-4 text-sm">
          Trip request not found
        </div>
      </div>
    );
  }

  const userDetails = getUserDetails(tripRequest);
  const tripDetails = getTripDetails(tripRequest);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Trip Request Detail</h1>
          <p className="mt-1 text-sm text-gray-500">Request ID: {tripRequest._id}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={downloadAsText}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download as Text
          </button>
          <button
            onClick={() => navigate('/trips/requests')}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Back
          </button>
        </div>
      </div>

      {/* User Details Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">USER DETAILS</h2>
        </div>
        <div className="p-6">
          {userDetails ? (
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {Object.entries(userDetails).map(([key, value]) => {
                if (key === '_id' && value === '—') return null;
                const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
                return (
                  <div key={key}>
                    <dt className="text-sm font-medium text-gray-500">{label}</dt>
                    <dd className="mt-1 text-sm text-gray-900 break-words">{formatValue(value)}</dd>
                  </div>
                );
              })}
            </dl>
          ) : (
            <p className="text-sm text-gray-500">No user details available</p>
          )}
        </div>
      </div>

      {/* Trip Details Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">TRIP DETAILS</h2>
        </div>
        <div className="p-6">
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {Object.entries(tripDetails).map(([key, value]) => {
              const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
              return (
                <div key={key}>
                  <dt className="text-sm font-medium text-gray-500">{label}</dt>
                  <dd className="mt-1 text-sm text-gray-900 break-words">{formatValue(value)}</dd>
                </div>
              );
            })}
          </dl>
        </div>
      </div>

      {/* Request Metadata */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">REQUEST METADATA</h2>
        </div>
        <div className="p-6">
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500">Request ID</dt>
              <dd className="mt-1 text-sm text-gray-900 font-mono break-all">{tripRequest._id}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Created At</dt>
              <dd className="mt-1 text-sm text-gray-900">{formatDate(tripRequest.createdAt)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Updated At</dt>
              <dd className="mt-1 text-sm text-gray-900">{formatDate(tripRequest.updatedAt)}</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
};

export default TripRequestDetail;
